import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CreditCard, Download, Calendar, AlertCircle, CheckCircle, XCircle, Loader2, DollarSign, Clock, Receipt } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import PageHeader from './ui/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingManagementPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      // Fetch subscription status
      const subResponse = await axios.get(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(subResponse.data);

      // Fetch payment history
      const historyResponse = await axios.get(`${API_URL}/api/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(historyResponse.data.transactions || []);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access at the end of the current billing period.')) {
      return;
    }

    try {
      setActionLoading('cancel');
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Subscription canceled successfully. You will retain access until the end of your billing period.');
      fetchBillingData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setActionLoading('update');
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/payments/update-method`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update payment method');
      setActionLoading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      setActionLoading('portal');
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/stripe/create-portal-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to open billing portal');
      setActionLoading(null);
    }
  };

  const downloadInvoice = async (transactionId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/payments/invoice/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${transactionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download invoice');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
          </div>
        </div>
      </div>
    );
  }

  const hasSubscription = subscription?.has_subscription;
  const subData = subscription?.subscription;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with Professional Billing Image */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-green-600 to-emerald-700 text-white shadow-xl mb-8">
          <div className="absolute inset-0 opacity-25">
            <img 
              src="https://images.unsplash.com/photo-1758518726324-62bef7c815b0?auto=format&fit=crop&w=1200&q=80" 
              alt="Professionals reviewing billing documents with satisfaction"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative px-8 py-12">
            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <h1 className="text-4xl md:text-5xl font-bold mb-3">Billing & Subscription</h1>
            <p className="text-green-100 text-lg">Manage your subscription, payments, and billing information</p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="subscription" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            <TabsTrigger value="history">Payment History</TabsTrigger>
          </TabsList>

          <TabsContent value="subscription" className="space-y-6">
            {hasSubscription ? (
              <>
                {/* Current Subscription Card */}
                <Card className="border-2 border-green-200">
                  <CardHeader className="bg-gradient-to-br from-green-50 to-green-100">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        Active Subscription
                      </CardTitle>
                      <Badge variant="default" className="bg-green-600">
                        {subData?.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-3">Plan Details</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-slate-700">Plan:</span>
                            <span className="font-semibold capitalize">{subData?.plan}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-700">Monthly Fee:</span>
                            <span className="font-semibold">${subData?.monthly_fee}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-700">Users:</span>
                            <span className="font-semibold">{subData?.user_count}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-slate-600 mb-3">Billing Cycle</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-slate-700">Next Billing Date:</span>
                            <span className="font-semibold">
                              {new Date(subData?.current_period_end).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-700">Status:</span>
                            <Badge variant="secondary">
                              {subData?.cancel_at_period_end ? 'Canceling' : 'Active'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t flex gap-3">
                      <Button
                        onClick={handleManageBilling}
                        disabled={actionLoading === 'portal'}
                        variant="outline"
                      >
                        {actionLoading === 'portal' ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                        ) : (
                          <><CreditCard className="w-4 h-4 mr-2" /> Manage Billing Portal</>
                        )}
                      </Button>

                      <Button
                        onClick={handleUpdatePaymentMethod}
                        disabled={actionLoading === 'update'}
                        variant="outline"
                      >
                        {actionLoading === 'update' ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                        ) : (
                          <>Update Payment Method</>
                        )}
                      </Button>

                      {!subData?.cancel_at_period_end && (
                        <Button
                          onClick={handleCancelSubscription}
                          disabled={actionLoading === 'cancel'}
                          variant="destructive"
                        >
                          {actionLoading === 'cancel' ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Canceling...</>
                          ) : (
                            <>Cancel Subscription</>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>No Active Subscription</CardTitle>
                  <CardDescription>Subscribe to access all features</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => window.location.href = '/subscription'}>
                    View Plans
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>View and download your payment receipts</CardDescription>
              </CardHeader>
              <CardContent>
                {paymentHistory.length > 0 ? (
                  <div className="space-y-3">
                    {paymentHistory.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold capitalize">{transaction.payment_type} Plan</p>
                            <p className="text-sm text-slate-600">
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold">${transaction.amount}</p>
                            <Badge
                              variant={transaction.payment_status === 'completed' ? 'default' : 'secondary'}
                            >
                              {transaction.payment_status}
                            </Badge>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadInvoice(transaction.id)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-600">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No payment history yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BillingManagementPage;